import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
  import {makeApolloClientCall, makeApolloClientMutationCall} from "../../Api/makeApolloApiCall";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import ReactTable from "../Components/ReactTable";
import SelectDropdown from "../Components/Dropdowns/SelectDropdown";
import ConfirmationModal from "../Components/ConfirmationModal";
import {wrapValueInOptionStructure} from "../../utils/maybeAddLabelToOptions";
import {toastError, toastSuccess} from "../../utils/toast";
import {AVAILABLE_PROJECTION_VERSIONS_QUERY} from "./Components/VersionSelect";
import {NUMBER_EXPRESSION_COLUMN} from "./Components/storeUniverseTableHelpers";
import {useProjectionLoadTimer} from "./Components/ProjectionLoadTimer";
import {CELL_TO_MARKETS_QUERY} from "./cellToMarkets";
import {copyTargetVersionOptions} from "./ProjectionMaintenanceUniverseTab";
import {serverErrorMessage} from "./ProjectionMaintenanceProjectionTab";

export const COPY_PROJECTION_CELL_TO_MARKETS_QUERY = `
    mutation($fromVersion: String!, $toVersion: String!) {
        copyProjectionCellToMarkets(fromVersion: $fromVersion, toVersion: $toVersion) {
            fromVersion
            toVersion
            marketNodes
            cellMarketEdges
        }
    }
`;

export const SOURCE_CELL_TO_MARKETS_CHECKING = 'checking';
export const SOURCE_CELL_TO_MARKETS_PRESENT = 'present';
export const SOURCE_CELL_TO_MARKETS_MISSING = 'missing';
export const SOURCE_CELL_TO_MARKETS_UNKNOWN = 'unknown';

export const sourceCellToMarketsWarningMessage = (sourceCellToMarketsStatus, version) => ({
    [SOURCE_CELL_TO_MARKETS_MISSING]: `No cell to markets were found for ${version}. The copy will fail until they are uploaded.`,
    [SOURCE_CELL_TO_MARKETS_UNKNOWN]: `Could not check whether ${version} has cell to markets.`,
}[sourceCellToMarketsStatus]);

export const copyCellToMarketButtonTitle = (version, sourceCellToMarketsStatus) => {
    if (!version) return 'Select a Projection Version first';
    if (sourceCellToMarketsStatus === SOURCE_CELL_TO_MARKETS_CHECKING) return `Checking for cell to markets in ${version}`;
    if (sourceCellToMarketsStatus === SOURCE_CELL_TO_MARKETS_MISSING) return `No cell to markets were found for ${version}`;
    return 'Copy the cell to market from selected projection version to another version';
};

export const copyCellToMarketDisabled = (version, sourceCellToMarketsStatus) =>
    !version
    || sourceCellToMarketsStatus === SOURCE_CELL_TO_MARKETS_CHECKING
    || sourceCellToMarketsStatus === SOURCE_CELL_TO_MARKETS_MISSING;

const TEXT_COLUMN = {filterFn: 'includesString'};

const cellToMarketsColumns = [
    {header: 'UNIVERSE', accessorKey: 'universe', ...TEXT_COLUMN},
    {header: 'REGION', accessorKey: 'region', ...TEXT_COLUMN},
    {header: 'GEOGRAPHIC REGION', accessorKey: 'geographic_region', ...TEXT_COLUMN},
    {header: 'MARKET TAG', accessorKey: 'market_tag', ...NUMBER_EXPRESSION_COLUMN},
    {header: 'NUCLEUS MARKET ID', accessorKey: 'nucleus_market_id', ...NUMBER_EXPRESSION_COLUMN},
    {header: 'MARKET', accessorKey: 'market', ...TEXT_COLUMN},
    {header: 'CELL ID', accessorKey: 'cell_id', ...NUMBER_EXPRESSION_COLUMN},
    {header: 'CELL DESCRIPTION', accessorKey: 'cell_description', size: 250, ...TEXT_COLUMN},
];

export const ProjectionMaintenanceCellToMarketsTab = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [projectionVersionOptions, setProjectionVersionOptions] = useState(undefined);
    const [projectionVersion, setProjectionVersion] = useState(undefined);

    const [submittedVersion, setSubmittedVersion] = useState(null);
    const [reloadCount, setReloadCount] = useState(0);
    const {startLoad, finishLoad, cancelLoad} = useProjectionLoadTimer();

    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [copyToVersion, setCopyToVersion] = useState('');
    const [copying, setCopying] = useState(false);
    const [sourceCellToMarketsStatus, setSourceCellToMarketsStatus] = useState(undefined);

    useEffect(() => {
        makeApolloClientCall(AVAILABLE_PROJECTION_VERSIONS_QUERY)
            .then((projectionVersionsResponse) => {
                setProjectionVersionOptions(projectionVersionsResponse.availableProjectionVersions
                    .map(version => wrapValueInOptionStructure(version.version)));
            })
            .catch(() => setProjectionVersionOptions([]));
    }, []);

    useEffect(() => {
        if (!submittedVersion) return;

        const failedMessage = `Failed to load the cell to markets for ${submittedVersion}`;

        setLoading(true);
        startLoad('Cell to Markets tab');
        makeApolloClientCall(CELL_TO_MARKETS_QUERY, false, {version: submittedVersion})
            .then((response) => {
                finishLoad();
                setData(response?.cellToMarkets ?? []);
            })
            .catch(() => {
                setData([]);
                toastError(toast, failedMessage);
            })
            .finally(() => {
                cancelLoad();
                setLoading(false);
            });
    }, [submittedVersion, reloadCount]);

    useEffect(() => {
        if (!projectionVersion) {
            setSourceCellToMarketsStatus(undefined);
            return undefined;
        }

        let cancelled = false;
        setSourceCellToMarketsStatus(SOURCE_CELL_TO_MARKETS_CHECKING);
        makeApolloClientCall(CELL_TO_MARKETS_QUERY, false, {version: projectionVersion})
            .then((response) => {
                if (cancelled) return;
                setSourceCellToMarketsStatus((response?.cellToMarkets?.length ?? 0) > 0
                    ? SOURCE_CELL_TO_MARKETS_PRESENT
                    : SOURCE_CELL_TO_MARKETS_MISSING);
            })
            .catch(() => {
                if (!cancelled) setSourceCellToMarketsStatus(SOURCE_CELL_TO_MARKETS_UNKNOWN);
            });
        return () => {
            cancelled = true;
        };
    }, [projectionVersion]);

    const closeCopyModal = () => {
        setCopyModalOpen(false);
        setCopyToVersion('');
    };

    const onCopyCellToMarketsConfirmed = () => {
        const fromVersion = projectionVersion;
        const toVersion = copyToVersion;

        setCopying(true);
        return makeApolloClientMutationCall(COPY_PROJECTION_CELL_TO_MARKETS_QUERY, {fromVersion, toVersion})
            .then((copyResponse) => {
                const {marketNodes, cellMarketEdges} = copyResponse.copyProjectionCellToMarkets;
                toastSuccess(toast, `Copied cell to markets from ${fromVersion} to ${toVersion} (${marketNodes} markets, ${cellMarketEdges} cell links)`);
                if (submittedVersion === toVersion) setReloadCount(count => count + 1);
                closeCopyModal();
            })
            .catch((error) => toastError(toast, `Failed to copy cell to market from ${fromVersion} to ${toVersion}: ${serverErrorMessage(error)}`))
            .finally(() => setCopying(false));
    };

    return (
        <div className={"projection-maintenance-projection-contents"}>
            <div className={'projection-toolbar'} data-testid={'cell_to_markets_version'}>
                <SelectDropdown id={"projection_version_dropdown"}
                                className={"optional-dropdown"}
                                dataTestId={"projection_version"}
                                inputLabel={"Projection Version"}
                                selectValue={projectionVersion}
                                options={projectionVersionOptions}
                                onChange={(option) => setProjectionVersion(option?.value)}
                />

                <button type={'button'}
                        className={'button primary projection-summary-submit-button'}
                        data-testid={'cell-to-markets-submit-button'}
                        disabled={!projectionVersion}
                        title={projectionVersion ? 'Load the cell to markets for the selected version' : 'Select a Projection Version first'}
                        onClick={() => setSubmittedVersion(projectionVersion)}
                >
                    Submit
                </button>

                <button type={'button'}
                        className={'button primary projection-copy-version-button'}
                        data-testid={'copy-cell-to-market-version-button'}
                        disabled={copyCellToMarketDisabled(projectionVersion, sourceCellToMarketsStatus)}
                        title={copyCellToMarketButtonTitle(projectionVersion, sourceCellToMarketsStatus)}
                        onClick={() => setCopyModalOpen(true)}
                >
                    Copy Cell to Market Version
                </button>
            </div>

            {submittedVersion && <LoadingWrapper loading={loading}>
                <ReactTable className={'projection-cell-to-markets-table'}
                            filterable
                            data={data}
                            columns={cellToMarketsColumns}
                            initialState={{pagination: {pageSize: 25}}}
                />
            </LoadingWrapper>}

            {copyModalOpen && <ConfirmationModal headerText={'Copy Cell to Market Version'}
                                                 confirmButtonText={'Copy'}
                                                 loading={copying}
                                                 submitButtonDisabled={!copyToVersion}
                                                 confirmationCallback={onCopyCellToMarketsConfirmed}
                                                 cancellationCallback={closeCopyModal}
            >
                {sourceCellToMarketsWarningMessage(sourceCellToMarketsStatus, projectionVersion) &&
                    <p className={'copy-cell-to-market-warning'} data-testid={'copy-cell-to-market-warning'}>
                        {sourceCellToMarketsWarningMessage(sourceCellToMarketsStatus, projectionVersion)}
                    </p>}
                <SelectDropdown id={'copy_cell_to_market_version_dropdown'}
                                inputId={'copy_cell_to_market_version_input'}
                                inputLabel={'Version'}
                                containerClassName={'copy-projection-version-field'}
                                dataTestId={'copy-cell-to-market-version'}
                                options={copyTargetVersionOptions(projectionVersion, projectionVersionOptions ?? [])}
                                selectValue={copyToVersion || undefined}
                                placeholder={'Select a Version...'}
                                menuPortalTarget={document.body}
                                styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
                                onChange={(option) => setCopyToVersion(option?.value ?? '')}
                />
            </ConfirmationModal>}
        </div>
    );
};
